import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {CSSProperties, ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import PortalDocument, {PortalDocumentInterface} from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import Parameter from "../../parameter/Parameter";
import {Dropdown} from "primereact/dropdown";
import LengthUnit from "../../../../shared/units/LengthUnit";
import FileInput, {File} from "../../../../shared/components/FileInput";
import {ColorPicker} from "primereact/colorpicker";
import {InputText} from "primereact/inputtext";
import PositionInput from "../../../../shared/components/PositionInput";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputNumber} from "primereact/inputnumber";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import {InputSwitch} from "primereact/inputswitch";
import PortalDocumentManager, {PortalDocumentManagerGlobals} from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import BackgroundDialogFields from "../../field/page/BackgroundDialogFields";
import validator from "validator";
import PdfViewer, {PdfViewerDefinition, PreviewState} from "../../../viewer/pdf/PdfViewer";
import {ViewerMode} from "../../../viewer/ViewerMode";
import DocumentSettings from "../../../document/DocumentSettings";
import {PdfPageDefinition} from "../../../viewer/pdf/PdfPage";
import MimeType from "../../../document/mimetype/MimeType";
import DropdownNumber from "../../../../shared/components/DropdownNumber";
import {
	BackgroundFunction,
	PortalBackgroundToolboxOptions
} from "../../../../shared/models/PortalBackgroundToolboxOptions";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {PortalBackgroundFileData} from "../../../../shared/models/PortalBackgroundFileData";
import {
	AdminWebserviceStatus,
	BackgroundBaseSettings,
	BackgroundPositionMode,
	BackgroundsDocument,
	BaseToolbox,
	ColorBackground,
	Functions,
	Information,
	MetadataRectangle,
	PagePages,
	PositionBackgroundBaseSettings,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	VisibilityBackgroundBaseSettings
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalToolboxOptionsOptions} from "../../../../shared/models/PortalToolboxOptionsOptions";
import {BackgroundMode, PortalAddBackground} from "../../../../shared/models/PortalAddBackground";
import {PortalPagesBackgroundBaseSettings} from "../../../../shared/models/PortalPagesBackgroundBaseSettings";
import PageMode from "../../../../shared/models/PageMode";
import Immutable from "../../../../shared/immutable/Immutable";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import UserSettings from "../../../session/UserSettings";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface BackgroundDialogProperties extends PortalDialogProperties<PortalToolboxOptionsOptions>,
	PortalDocumentManagerGlobals {
	userSettings: Immutable<UserSettings>;
}

interface BackgroundDialogStatus extends PortalDialogState<PortalToolboxOptionsOptions>, PreviewState {
	hasBackground: boolean
	pages: number;
	imageFile: File;
	unit: LengthUnit;
	imageWidth: number;
	imageHeight: number;
}

export class BackgroundDialogDefinition extends PortalDialog<PortalToolboxOptionsOptions,
	BackgroundDialogProperties, BackgroundDialogStatus> {

	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			previewZoomValue: 0,
			renderPreview: false,
			pages: 1,
			imageFile: {
				name: "",
				mimetype: "image/png",
				value: "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
			},
			hasBackground: false,
			fieldErrors: new Map<BackgroundDialogFields, string>(),
			unit: LengthUnit.MILLIMETER,
			imageWidth: 1,
			imageHeight: 1
		};

		this.onChange = this.onChange.bind(this);
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editBackground, Functions.getEditBackgroundDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.BACKGROUND, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["background-dialog", "verylarge"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}
					onShow={() => this.setState({
						renderPreview: true
					})}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.background.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
					{this.state.renderPreview && this.renderPreview()}
				</div>
			</Dialog>
		);
	}

	private getPageRelativeScaling(
		boundingWidth: number, boundingHeight: number, width: number, height: number, rotation: number
	): number {
		let radians: number = (rotation % 180) * Math.PI / 180;
		return Math.min(
			boundingHeight / (Math.abs(Math.sin(radians)) * width + Math.abs(Math.cos(radians)) * height),
			boundingWidth / (Math.abs(Math.sin(radians)) * height + Math.abs(Math.cos(radians)) * width)
		);
	};

	public renderPreview(): ReactNode {
		let parameter: PortalBackgroundToolboxOptions | undefined =
			this.state.parameter?.data?.background as PortalBackgroundToolboxOptions;
		let selected: PortalDocumentInterface | undefined = this.props.documentSelection.get().pop();

		if (!FunctionHelper.exists(parameter?.add) || !FunctionHelper.exists(selected) ||
			parameter.mode === BackgroundFunction.REMOVE) {
			return null;
		}

		let addParameter: PortalAddBackground = parameter.add as PortalAddBackground;
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(selected!.id!);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let page: PagePages | undefined = portalDocument.getPage(documentSettings.activeViewer.currentPage);
		let previewScale: number = this.state.previewZoomValue / 100;

		let width: number = 0;
		let height: number = 0;
		let offsetLeft: number = 0;
		let offsetTop: number = 0;
		let scale: number = previewScale;
		let rotation: number = 0;
		let position: BackgroundPositionMode = BackgroundPositionMode.CenterCenter;
		let styleLeft: string = "0";
		let styleTop: string = "0";
		let pageWidth: number = 0;
		let pageHeight: number = 0;

		switch (PdfPageDefinition.calculateRotationSteps(
			FunctionHelper.applyValue(page?.rotation, PagePages.getRotationDefault())
		)) {
			case 0:
			case 2:
				pageWidth = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.width, page?.width, MetadataRectangle.getWidthDefault()
				);
				pageHeight = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.height, page?.height, MetadataRectangle.getHeightDefault()
				);
				break;
			case 1:
			case 3:
				pageWidth = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.height, page?.height, MetadataRectangle.getHeightDefault()
				);
				pageHeight = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.width, page?.width, MetadataRectangle.getWidthDefault()
				);
				break;
		}

		pageWidth = UnitHelper.unitToPx(pageWidth, LengthUnit.POINTS, this.props.portalResolution);
		pageHeight = UnitHelper.unitToPx(pageHeight, LengthUnit.POINTS, this.props.portalResolution);
		let imageWidth: number = this.state.imageWidth;
		let imageHeight: number = this.state.imageHeight;

		if (addParameter.mode === BackgroundMode.IMAGE) {
			scale *= FunctionHelper.applyValue(
				addParameter.imageBackground?.settings?.scale, BackgroundBaseSettings.getScaleDefault()
			) / 100;

			offsetLeft = UnitHelper.unitToPx(FunctionHelper.applyValue(
				addParameter.imageBackground?.settings?.position?.x, PositionBackgroundBaseSettings.getXDefault()
			), this.state.unit, this.props.portalResolution);
			offsetLeft *= previewScale;
			offsetTop = UnitHelper.unitToPx(FunctionHelper.applyValue(
				addParameter.imageBackground?.settings?.position?.y, PositionBackgroundBaseSettings.getYDefault()
			), this.state.unit, this.props.portalResolution);
			offsetTop *= previewScale;
			position = FunctionHelper.applyValue(addParameter.imageBackground?.settings?.position?.position, position);

			rotation = FunctionHelper.applyValue(
				addParameter.imageBackground?.settings?.rotation, BackgroundBaseSettings.getRotationDefault()
			);
			if (addParameter.imageBackground?.settings?.relativeToPage) {
				scale *= this.getPageRelativeScaling(
					pageWidth, pageHeight, imageWidth, imageHeight, rotation
				);
			}

			width = imageWidth * scale / 2;
			height = imageHeight * scale / 2;
		} else {
			offsetLeft = UnitHelper.unitToPx(FunctionHelper.applyValue(
				addParameter.colorBackground?.settings?.position?.x, PositionBackgroundBaseSettings.getXDefault()
			), this.state.unit, this.props.portalResolution);
			offsetLeft *= previewScale;
			offsetTop = UnitHelper.unitToPx(FunctionHelper.applyValue(
				addParameter.colorBackground?.settings?.position?.y, PositionBackgroundBaseSettings.getYDefault()
			), this.state.unit, this.props.portalResolution);
			offsetTop *= previewScale;
			position = FunctionHelper.applyValue(
				addParameter.colorBackground?.settings?.position?.position, position
			);

			rotation = FunctionHelper.applyValue(
				addParameter.colorBackground?.settings?.rotation, BackgroundBaseSettings.getRotationDefault()
			);
			if (addParameter.colorBackground?.settings?.relativeToPage) {
				scale *= FunctionHelper.applyValue(
					addParameter.colorBackground?.settings?.scale, BackgroundBaseSettings.getScaleDefault()
				) / 100;
				scale *= this.getPageRelativeScaling(
					pageWidth, pageHeight, pageWidth, pageHeight, rotation
				);
			}

			width = pageWidth * scale / 2;
			height = pageHeight * scale / 2;
		}

		switch (position) {
			case BackgroundPositionMode.TopLeft:
			case BackgroundPositionMode.CenterLeft:
			case BackgroundPositionMode.BottomLeft:
				styleLeft = (width + offsetLeft) + LengthUnit.PIXEL;
				break;
			case BackgroundPositionMode.TopCenter:
			case BackgroundPositionMode.CenterCenter:
			case BackgroundPositionMode.BottomCenter:
				styleLeft = "calc(50% + " + offsetLeft + LengthUnit.PIXEL + ")";
				break;
			case BackgroundPositionMode.TopRight:
			case BackgroundPositionMode.CenterRight:
			case BackgroundPositionMode.BottomRight:
				styleLeft = "calc(100% + " + (-width - offsetLeft) + LengthUnit.PIXEL + ")";
				break;
		}

		switch (position) {
			case BackgroundPositionMode.TopLeft:
			case BackgroundPositionMode.TopCenter:
			case BackgroundPositionMode.TopRight:
				styleTop = (height + offsetTop) + LengthUnit.PIXEL;
				break;
			case BackgroundPositionMode.CenterLeft:
			case BackgroundPositionMode.CenterCenter:
			case BackgroundPositionMode.CenterRight:
				styleTop = "calc(50% + " + (-offsetTop) + LengthUnit.PIXEL + ")";
				break;
			case BackgroundPositionMode.BottomLeft:
			case BackgroundPositionMode.BottomCenter:
			case BackgroundPositionMode.BottomRight:
				styleTop = "calc(100% + " + (-height - offsetTop) + LengthUnit.PIXEL + ")";
				break;
		}

		let colorStyle: CSSProperties = {
			background: FunctionHelper.applyValue(
				addParameter.colorBackground?.color, ColorBackground.getColorDefault()
			),
			opacity: (FunctionHelper.applyValue(
				addParameter.colorBackground?.settings?.opacity, BackgroundBaseSettings.getOpacityDefault()
			) / 100).toString(),
			transform: "rotate(" + rotation + "deg) scale(" + scale + ") translate(-50%, -50%)",
			left: styleLeft,
			top: styleTop,
			width: pageWidth,
			height: pageHeight
		};

		let imageStyle: CSSProperties = {
			opacity: (FunctionHelper.applyValue(
				addParameter.imageBackground?.settings?.opacity, BackgroundBaseSettings.getOpacityDefault()
			) / 100).toString(),
			transform: "rotate(" + rotation + "deg) scale(" + scale + ") translate(-50%, -50%)",
			left: styleLeft,
			top: styleTop
		};

		return (
			<div className="p-col-12 p-xl-4 p-lg-hidden p-grid divider-left"
				 hidden={!this.state.fields?.contains(BackgroundDialogFields.PREVIEW)}>
				<div className="dialog-preview" style={{
					maxWidth: previewScale === 0 ? "100%" : Math.round(pageWidth * previewScale),
					maxHeight: previewScale === 0 ? "100%" : Math.round(pageHeight * previewScale)
				}}>
					<PdfViewer {...PdfViewerDefinition.defaults()} {...this.props} viewerMode={ViewerMode.PAGE}
							   portalDocument={Immutable.from(selected!)}
							   currentPage={documentSettings.activeViewer.currentPage}
							   updatePreviewState={this.setState.bind(this)}/>
					<div className="preview-layer">
						<img hidden={addParameter.mode !== BackgroundMode.IMAGE} style={imageStyle}
							 alt={Translate.value("background.preview.alt", {ns: "dialog"})}
							 src={"data:" + this.state.imageFile.mimetype + ";base64," + this.state.imageFile.value}/>
						<div className="color-background" hidden={addParameter.mode !== BackgroundMode.COLOR}
							 style={colorStyle}/>
					</div>
				</div>
			</div>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalBackgroundToolboxOptions | undefined =
			this.state.parameter?.data?.background as PortalBackgroundToolboxOptions;
		let errors: Map<BackgroundDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalAddBackground = parameter.add as PortalAddBackground;
		let settings: BackgroundBaseSettings | undefined =
			(
				(addParameter.mode === BackgroundMode.IMAGE) ?
					addParameter.imageBackground?.settings : addParameter.colorBackground?.settings
			) as BackgroundBaseSettings;
		let pages: PortalPagesBackgroundBaseSettings | undefined = settings?.pages;

		if (!FunctionHelper.exists(settings) || !FunctionHelper.exists(pages)) {
			return null;
		}

		return (
			<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
				<fieldset className="p-col-12 p-grid" hidden={
					(!this.state.hasBackground || !this.state.fields?.contains(BackgroundDialogFields.MODE)) &&
					!this.state.fields?.contains(BackgroundDialogFields.TYPE)
				}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.hasBackground || !this.state.fields?.contains(BackgroundDialogFields.MODE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-mode">
							<Translate {...this.props} data="background.mode.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-mode"
									  value={FunctionHelper.applyValue(
										  parameter.mode, PortalBackgroundToolboxOptions.getModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "background.mode.remove", {ns: "dialog"}
											  ),
											  value: BackgroundFunction.REMOVE
										  },
										  {
											  label: Translate.value(
												  "background.mode.add", {ns: "dialog"}
											  ),
											  value: BackgroundFunction.ADD
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  BackgroundDialogFields.MODE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid" hidden={
						parameter.mode === BackgroundFunction.REMOVE ||
						!this.state.fields?.contains(BackgroundDialogFields.TYPE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-type">
							<Translate {...this.props} data="background.type.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-type"
									  value={FunctionHelper.applyValue(
										  addParameter.mode, PortalAddBackground.getModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "background.type.color", {ns: "dialog"}
											  ),
											  value: BackgroundMode.COLOR
										  },
										  {
											  label: Translate.value(
												  "background.type.image", {ns: "dialog"}
											  ),
											  value: BackgroundMode.IMAGE
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  BackgroundDialogFields.TYPE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					parameter.mode === BackgroundFunction.REMOVE
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 addParameter.mode !== BackgroundMode.COLOR ||
							 !this.state.fields?.contains(BackgroundDialogFields.COLOR)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-color">
							<Translate {...this.props} data="background.display.color" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className={"p-inputgroup" + (
								errors.has(BackgroundDialogFields.COLOR) ? " p-invalid" : ""
							)}>
								<InputText className={errors.has(BackgroundDialogFields.COLOR) ? "p-invalid" : ""}
										   tooltip={errors.has(BackgroundDialogFields.COLOR) ? Translate.value(
											   errors.get(BackgroundDialogFields.COLOR)!, {ns: "message"}
										   ) : ""}
										   value={FunctionHelper.applyValue(
											   addParameter.colorBackground?.color, ColorBackground.getColorDefault()
										   )}
										   onInput={(e) => this.onChange(
											   BackgroundDialogFields.COLOR, e.currentTarget.value
										   )}/>
								<ColorPicker inputId="control-color" className="p-inputgroup-addon" format="hex"
											 defaultColor={
												 ColorBackground.getColorDefault().replace("#", "")
											 }
											 value={FunctionHelper.applyValue(
												 addParameter.colorBackground?.color, ColorBackground.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 BackgroundDialogFields.COLOR, "#" + e.value
											 )} appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(BackgroundDialogFields.COLOR) ?
								Translate.value(
									errors.get(BackgroundDialogFields.COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 addParameter.mode !== BackgroundMode.IMAGE ||
							 !this.state.fields?.contains(BackgroundDialogFields.IMAGE)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-image-file">
							<Translate {...this.props} data="background.display.file" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<FileInput inputId="control-image-file"
									   value={FunctionHelper.applyValue(
										   addParameter.imageBackground?.image?.value, ""
									   )}
									   filename={this.state.imageFile.name} accept="image/gif, image/png, image/jpeg"
									   onChange={(files) => this.onChange(
										   BackgroundDialogFields.IMAGE, files[0]
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"/>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(BackgroundDialogFields.POSITION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-position">
							<Translate {...this.props} data="background.display.position" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<PositionInput
								value={FunctionHelper.applyValue(
									settings.position?.position, BackgroundPositionMode.CenterCenter
								)}
								onChange={(value) => this.onChange(BackgroundDialogFields.POSITION, value)}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !this.state.fields?.contains(BackgroundDialogFields.MARGIN_LEFT) &&
							 !this.state.fields?.contains(BackgroundDialogFields.MARGIN_TOP)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-left"
							   hidden={!this.state.fields?.contains(BackgroundDialogFields.MARGIN_LEFT)}>
							<Translate {...this.props} data="background.display.offset.horizontal" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-left" step={1} suffix={" " + this.state.unit}
											 showButtons={true} useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 settings.position?.x, PositionBackgroundBaseSettings.getXDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 min={PositionBackgroundBaseSettings.getXMin()}
											 onChange={(e) => this.onChange(
												 BackgroundDialogFields.MARGIN_LEFT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												BackgroundDialogFields.MARGIN_LEFT,
												PositionBackgroundBaseSettings.getXDefault()
											)}/>
								</div>
							</div>
						</div>

						<label className="p-col-12 p-sm-5" htmlFor="control-margin-top"
							   hidden={!this.state.fields?.contains(BackgroundDialogFields.MARGIN_TOP)}>
							<Translate {...this.props} data="background.display.offset.vertical" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-top" step={1} suffix={" " + this.state.unit}
											 showButtons={true} useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 settings.position?.y, PositionBackgroundBaseSettings.getYDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 min={PositionBackgroundBaseSettings.getYMin()}
											 onChange={(e) => this.onChange(
												 BackgroundDialogFields.MARGIN_TOP, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												BackgroundDialogFields.MARGIN_TOP,
												PositionBackgroundBaseSettings.getYDefault()
											)}/>
								</div>
							</div>
						</div>
					</div>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.elements.position"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(BackgroundDialogFields.RELATIVE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-relative"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-relative"
										 checked={FunctionHelper.applyValue(
											 settings.relativeToPage, BackgroundBaseSettings.getRelativeToPageDefault()
										 )}
										 onChange={(e) => this.onChange(
											 BackgroundDialogFields.RELATIVE, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-relative">
								<Translate {...this.props} data="background.display.relative.label" namespace="dialog"/>
							</label>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-scale"
							   hidden={
								   (addParameter.mode !== BackgroundMode.IMAGE && !settings.relativeToPage) ||
								   !this.state.fields?.contains(BackgroundDialogFields.SCALE)
							   }>
							<Translate {...this.props} data="background.display.scale" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7"
							 hidden={
								 (addParameter.mode !== BackgroundMode.IMAGE && !settings.relativeToPage) ||
								 !this.state.fields?.contains(BackgroundDialogFields.SCALE)
							 }>
							<DropdownNumber inputId="control-scale" showButtons={true} suffix=" %"
											value={FunctionHelper.applyValue(
												settings.scale, BackgroundBaseSettings.getScaleDefault()
											)}
											onChange={(e) => this.onChange(
												BackgroundDialogFields.SCALE, e.value
											)}
											appendTo={document.getElementById("root")}
											min={BackgroundBaseSettings.getScaleMin()}
											options={[
												{label: "10", value: 10},
												{label: "25", value: 25},
												{label: "50", value: 50},
												{label: "75", value: 75},
												{label: "100", value: 100}
											]}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(BackgroundDialogFields.OPACITY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-opacity">
							<Translate {...this.props} data="background.display.opacity" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-opacity" step={1} showButtons={true} suffix=" %"
										 value={FunctionHelper.applyValue(
											 settings.opacity, BackgroundBaseSettings.getOpacityDefault()
										 )}
										 min={BackgroundBaseSettings.getOpacityMin()} useGrouping={false}
										 max={BackgroundBaseSettings.getOpacityMax()}
										 onChange={(e) => this.onChange(
											 BackgroundDialogFields.OPACITY, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(BackgroundDialogFields.ROTATION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-rotation">
							<Translate {...this.props} data="background.display.rotation" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-rotation" showButtons={true} step={1}
											value={FunctionHelper.applyValue(
												settings.rotation, BackgroundBaseSettings.getRotationDefault()
											)}
											onChange={(e) => this.onChange(
												BackgroundDialogFields.ROTATION, e.value
											)}
											appendTo={document.getElementById("root")} suffix=" °"
											min={BackgroundBaseSettings.getRotationMin()}
											max={BackgroundBaseSettings.getRotationMax()}
											options={[
												{label: "0", value: 0},
												{label: "45", value: 45},
												{label: "90", value: 90},
												{label: "135", value: 135},
												{label: "180", value: 180},
												{label: "225", value: 225},
												{label: "270", value: 270},
												{label: "315", value: 315}
											]}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					parameter.mode === BackgroundFunction.REMOVE
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(BackgroundDialogFields.VISIBILITY_PRINT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-print"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch id="control-print"
										 checked={FunctionHelper.applyValue(
											 settings.visibility?.onPrint,
											 VisibilityBackgroundBaseSettings.getOnPrintDefault()
										 )}
										 onChange={(e) => this.onChange(
											 BackgroundDialogFields.VISIBILITY_PRINT, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-print">
								<Translate {...this.props} data="background.visibility.print.label" namespace="dialog"/>
							</label>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(BackgroundDialogFields.VISIBILITY_SCREEN)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-screen"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch id="control-screen"
										 checked={FunctionHelper.applyValue(
											 settings.visibility?.onScreen,
											 VisibilityBackgroundBaseSettings.getOnScreenDefault()
										 )}
										 onChange={(e) => this.onChange(
											 BackgroundDialogFields.VISIBILITY_SCREEN, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-screen">
								<Translate {...this.props} data="background.visibility.screen.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>
				<Helper {...this.props}>
					<Translate {...this.props} data={"dialog.background.pages"} namespace={"help"} markdown={true}/>
				</Helper>
				<fieldset className="p-col-12 p-grid" hidden={
					parameter.mode === BackgroundFunction.REMOVE
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(BackgroundDialogFields.PAGE_FROM)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pages-from">
							<Translate {...this.props} data="background.pages.from" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-pages-from" showButtons={true} step={1} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 pages?.from, PortalPagesBackgroundBaseSettings.getFromDefault()
										 )}
										 min={PortalPagesBackgroundBaseSettings.getFromMin()} max={this.state.pages}
										 onChange={(e) => this.onChange(
											 BackgroundDialogFields.PAGE_FROM, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(BackgroundDialogFields.PAGE_TO)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pages-to">
							<Translate {...this.props} data="background.pages.to" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-pages-to" showButtons={true} step={1} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 pages?.to, PortalPagesBackgroundBaseSettings.getToDefault()
										 )}
										 min={PortalPagesBackgroundBaseSettings.getToMin()} max={this.state.pages}
										 onChange={(e) => this.onChange(
											 BackgroundDialogFields.PAGE_TO, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(BackgroundDialogFields.PAGE_MODE)}>
						<label className="p-col-12 p-sm-5">
							<Translate {...this.props} data="background.pages.odd_even" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-filter"
									  value={FunctionHelper.applyValue(
										  pages?.mode, PortalPagesBackgroundBaseSettings.getModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "background.pages.filter.all", {ns: "dialog"}
											  ),
											  value: PageMode.All
										  },
										  {
											  label: Translate.value(
												  "background.pages.filter.even", {ns: "dialog"}
											  ),
											  value: PageMode.Even
										  },
										  {
											  label: Translate.value(
												  "background.pages.filter.odd", {ns: "dialog"}
											  ),
											  value: PageMode.Odd
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  BackgroundDialogFields.PAGE_MODE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>
			</div>
		);
	}

	protected onChange(field: BackgroundDialogFields, value: any): void {
		let parameter: PortalBackgroundToolboxOptions | undefined =
			this.state.parameter!.data!.background as PortalBackgroundToolboxOptions;
		let addParameter: PortalAddBackground | undefined =
			this.state.parameter!.data!.background!.add as PortalAddBackground;
		let errors: Map<BackgroundDialogFields, string> = this.state.fieldErrors;

		let settings: BackgroundBaseSettings | undefined = (
			(addParameter.mode === BackgroundMode.IMAGE) ?
				addParameter.imageBackground?.settings : addParameter.colorBackground?.settings
		) as BackgroundBaseSettings;
		let pages: PortalPagesBackgroundBaseSettings | undefined = settings.pages as PortalPagesBackgroundBaseSettings;

		switch (field) {
			case BackgroundDialogFields.MODE:
				parameter.mode = value;
				break;
			case BackgroundDialogFields.TYPE:
				addParameter.mode = value;
				break;
			case BackgroundDialogFields.COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				addParameter.colorBackground!.color = value;
				break;
			case BackgroundDialogFields.IMAGE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let file: File = value;
				let fileDataType: PortalBackgroundFileData = PortalBackgroundFileData.fromJson({
					...file
				});
				addParameter.imageBackground!.image = fileDataType;

				let preRenderedImage: HTMLImageElement = new Image();
				preRenderedImage.onload = () => {
					this.setState({
						imageFile: file,
						imageWidth: preRenderedImage.naturalWidth,
						imageHeight: preRenderedImage.naturalHeight,
						parameter: this.state.parameter
					});
				};
				preRenderedImage.src = "data:" + file.mimetype + ";base64," + file.value;
				return;
			case BackgroundDialogFields.POSITION:
				settings.position!.position = value;
				break;
			case BackgroundDialogFields.MARGIN_LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PositionBackgroundBaseSettings.getXDefault();
				}

				settings.position!.x = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case BackgroundDialogFields.MARGIN_TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PositionBackgroundBaseSettings.getYDefault();
				}

				settings.position!.y = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case BackgroundDialogFields.RELATIVE:
				settings.relativeToPage = value;
				break;
			case BackgroundDialogFields.SCALE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = BackgroundBaseSettings.getScaleDefault();
				}

				settings.scale = value;
				break;
			case BackgroundDialogFields.OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = BackgroundBaseSettings.getOpacityDefault();
				}

				settings.opacity = value;
				break;
			case BackgroundDialogFields.ROTATION:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = BackgroundBaseSettings.getRotationDefault();
				}

				settings.rotation = value;
				break;
			case BackgroundDialogFields.VISIBILITY_PRINT:
				settings.visibility!.onPrint = value;
				break;
			case BackgroundDialogFields.VISIBILITY_SCREEN:
				settings.visibility!.onScreen = value;
				break;
			case BackgroundDialogFields.PAGE_FROM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalPagesBackgroundBaseSettings.getFromDefault();
				}

				pages.from = value;
				break;
			case BackgroundDialogFields.PAGE_TO:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalPagesBackgroundBaseSettings.getToDefault();
				}

				pages.to = value;
				break;
			case BackgroundDialogFields.PAGE_MODE:
				pages.mode = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalBackgroundToolboxOptions | undefined = this.state.parameter?.data?.background;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter?.add) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		let addParameter: PortalAddBackground = parameter!.add as PortalAddBackground;
		let hasBackground: boolean = FunctionHelper.applyValue(
			portalDocument.metadata?.backgrounds?.background, BackgroundsDocument.getBackgroundDefault()
		).length > 0;
		let pageCount: number = await portalDocument.getNumberOfPages();
		let currentPage: number = portalDocument.getSettings().activeViewer.currentPage;

		if (!hasBackground) {
			parameter!.mode = BackgroundFunction.ADD;
		}

		let imagePages: PortalPagesBackgroundBaseSettings | undefined = addParameter.imageBackground?.settings?.pages;
		if (!FunctionHelper.exists(imagePages)) {
			return;
		}

		imagePages!.from = currentPage;
		imagePages!.to = pageCount;

		let colorPages: PortalPagesBackgroundBaseSettings | undefined =
			addParameter.colorBackground?.settings?.pages;

		if (!FunctionHelper.exists(colorPages)) {
			return;
		}

		colorPages!.from = currentPage;
		colorPages!.to = pageCount;

		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());
		let imageFile: File = this.state.imageFile;
		let fileDataType: PortalBackgroundFileData | undefined =
			addParameter.imageBackground?.image as PortalBackgroundFileData;
		if (fileDataType.value !== "" && fileDataType.mimetype !== "" && fileDataType.name !== "") {
			this.onChange(BackgroundDialogFields.IMAGE, {
				name: fileDataType.name!,
				mimetype: fileDataType.mimetype!,
				value: fileDataType.value!
			});
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			imageFile: imageFile,
			hasBackground: hasBackground,
			previewZoomValue: 0,
			unit: userSettings.unit
		});
	};

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(): void {
		this.setState({
			renderPreview: false
		});

		super.hide();
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-background small"}/>
					<Translate {...this.props} data="background.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-grid">
				<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid">
							<div className="p-col-12 p-grid">
								<Button label={Translate.value("buttons.reset")} icon={"icon icon-rotate-270 small"}
										disabled={this.state.submitted} className="reset-button"
										onClick={() => this.resetParameter()}/>
								<Button label={Translate.value("buttons.submit")} disabled={!this.isValid()}
										icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
										className="primary-button" onClick={() => this.submit()}/>
								{this.state.submitted ?
									<Button label={Translate.value("buttons.close")} icon="icon icon-close"
											onClick={() => this.hide()} className="dialog-button"/> :
									<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
											onClick={() => this.hide()} className="dialog-button"/>
								}
							</div>
						</div>
					</fieldset>
				</div>
				<div className="p-col-12 p-xl-4 p-lg-hidden p-grid divider-left-hidden"></div>
			</div>
		);
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		let addParameter: PortalAddBackground | undefined =
			this.state.parameter!.data.background!.add as PortalAddBackground;
		let settings: BackgroundBaseSettings | undefined =
			(
				(addParameter.mode === BackgroundMode.IMAGE) ?
					addParameter.imageBackground?.settings :
					addParameter.colorBackground?.settings
			) as BackgroundBaseSettings;
		let pages: PortalPagesBackgroundBaseSettings | undefined =
			settings.pages as PortalPagesBackgroundBaseSettings;

		pages.pageInterval!.pages = pages.from + "-" + pages.to;
		pages.pageInterval!.odd = pages.mode === PageMode.Odd || pages.mode === PageMode.All
		pages.pageInterval!.even = pages.mode === PageMode.Even || pages.mode === PageMode.All

		this.setState({
			parameter: this.state.parameter
		}, async () => {
			await super.submit();

			let backgroundOptionsParameter: PortalToolboxOptionsOptions = this.state.parameter!.data.clone();
			let backgroundParameter: PortalBackgroundToolboxOptions =
				backgroundOptionsParameter.background as PortalBackgroundToolboxOptions;

			if (backgroundParameter.mode === BackgroundFunction.REMOVE) {
				backgroundParameter.add = undefined;
			} else {
				backgroundParameter.remove = undefined;

				let addParameter: PortalAddBackground =
					backgroundParameter?.add as PortalAddBackground;
				if (addParameter.mode === BackgroundMode.IMAGE) {
					addParameter.colorBackground = undefined;
				} else {
					addParameter.imageBackground = undefined;
				}
			}

			let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
				options: backgroundOptionsParameter.toJson()
			});

			let operationData: Array<BaseToolbox> = [toolboxOperation];
			let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
			let documentManager: PortalDocumentManager = this.props.context.documentManager;
			let selection: Array<PortalDocument> = documentManager.getSelection();

			for (let portalDocument of selection) {
				try {
					await this.props.context.session.executeRequest({
						webServiceType: webServiceType,
						document: portalDocument,
						operation: operationData,
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as SettingsInterface)
					});
				} catch (exception: any) {
					if (await this.onError(exception)) {
						await this.submit();
					}
					return;
				}

				let message: Message = new Message("success.toolbox.attachment.add", MessageType.SUCCESS);
				message.context = {filename: '"' + portalDocument.getFileName() + '" '};
				this.props.context.getPage().addMessage(message);
				this.hide();
			}
		});
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.BACKGROUND;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxOptionsOptions> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const BackgroundDialog = React.memo(BackgroundDialogDefinition,
	(prevProps: Readonly<BackgroundDialogProperties>, nextProps: Readonly<BackgroundDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default BackgroundDialog;